<template>
    <div class="flex align-items-center justify-content-center min-h-screen p-4 bg-gray-50">
        <div class="card p-6 shadow-2 border-round-xl text-center surface-card" style="max-width: 450px; width: 100%">

            <!-- Standalone card tokenization (add / replace card from settings) -->
            <template v-if="isCardTokenizeReturn">
                <div class="flex align-items-center justify-content-center mb-4">
                    <div class="bg-green-100 border-circle flex align-items-center justify-content-center"
                        style="width: 80px; height: 80px">
                        <i class="pi pi-credit-card text-4xl text-green-600"></i>
                    </div>
                </div>
                <h1 class="text-2xl font-bold text-900 mb-2">Card Verified!</h1>
                <p class="text-600 mb-2 line-height-3">
                    Your payment method has been saved successfully.
                </p>
                <p class="text-500 text-sm mb-5 line-height-3">
                    The <strong>LKR 30.00</strong> confirmation hold placed by PayHere is an intent payment —
                    <strong>not a charge</strong>. It will be reversed and the refund should reach your card
                    within <strong>1 business day</strong>.
                </p>
                <div class="flex flex-column gap-3">
                    <Button label="Go to Settings" icon="pi pi-arrow-right" iconPos="right"
                        class="p-button-raised continue-btn" @click="returnToOnboarding" />
                </div>
            </template>

            <!-- Free plan: card tokenization return (onboarding) -->
            <template v-else-if="isOnboardingReturn && isFreePlan">
                <div class="flex align-items-center justify-content-center mb-4">
                    <div class="bg-blue-100 border-circle flex align-items-center justify-content-center"
                        style="width: 80px; height: 80px">
                        <i class="pi pi-credit-card text-4xl text-blue-600"></i>
                    </div>
                </div>
                <h1 class="text-2xl font-bold text-900 mb-2">Card Verified!</h1>
                <p class="text-600 mb-3 line-height-3">
                    Your card has been verified successfully. The LKR 30.00 verification charge will be
                    reversed by PayHere — refund times may vary.
                </p>
                <p class="text-500 text-sm mb-5">Returning to complete your subscription setup...</p>
                <div class="flex flex-column gap-3">
                    <Button label="Continue Setup" icon="pi pi-arrow-right" iconPos="right"
                        class="p-button-raised continue-btn" @click="returnToOnboarding" />
                </div>
            </template>

            <!-- Settings: subscription selection return (paid checkout or tokenization) -->
            <template v-else-if="isSettingsReturn">
                <div class="flex align-items-center justify-content-center mb-4">
                    <div class="bg-green-100 border-circle flex align-items-center justify-content-center"
                        style="width: 80px; height: 80px">
                        <i class="pi pi-check text-4xl text-green-600"></i>
                    </div>
                </div>
                <h1 class="text-2xl font-bold text-900 mb-2">Subscription Updated!</h1>
                <p class="text-600 mb-5 line-height-3">
                    Your subscription is being activated. Return to settings to view your updated plan.
                </p>
                <div class="flex flex-column gap-3">
                    <Button label="Return to Settings" icon="pi pi-arrow-right" iconPos="right"
                        class="p-button-raised continue-btn" @click="returnToOnboarding" />
                </div>
            </template>

            <!-- Paid plan: checkout return (onboarding) -->
            <template v-else-if="isOnboardingReturn && !isFreePlan">
                <div class="flex align-items-center justify-content-center mb-4">
                    <div class="bg-green-100 border-circle flex align-items-center justify-content-center"
                        style="width: 80px; height: 80px">
                        <i class="pi pi-check text-4xl text-green-600"></i>
                    </div>
                </div>
                <h1 class="text-2xl font-bold text-900 mb-2">Payment Successful!</h1>
                <p class="text-500 text-sm mb-5">Almost there! Click below to complete your registration.</p>
                <div class="flex flex-column gap-3">
                    <Button label="Complete Registration" icon="pi pi-arrow-right" iconPos="right"
                        class="p-button-raised continue-btn" @click="returnToOnboarding" />
                </div>
            </template>

            <!-- Default payment success state -->
            <template v-else>
                <div class="flex align-items-center justify-content-center mb-4">
                    <div class="bg-green-100 border-circle flex align-items-center justify-content-center"
                        style="width: 80px; height: 80px">
                        <i class="pi pi-check text-4xl text-green-600"></i>
                    </div>
                </div>
                <h1 class="text-2xl font-bold text-900 mb-2">Payment Successful!</h1>
                <p class="text-600 mb-5 line-height-3 text-justify">
                    Thank you for your payment. Your transaction has been completed successfully. You can now access the
                    purchased package and enjoy the benefits it offers.
                </p>
                <div class="flex flex-column gap-3">
                    <Button
                        label="Go to Dashboard"
                        icon="pi pi-home"
                        class="p-button-raised"
                        @click="goDashboard"
                    />
                </div>
            </template>

        </div>
    </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import Button from 'primevue/button';
import { useRouter } from 'vue-router';
import { useAuthStore } from '@/stores/authStore';

const STUDENT_CARD_TOKENIZE_KEY       = 'studentCardTokenizePending';
const TUTOR_CARD_TOKENIZE_KEY         = 'tutorCardTokenizePending';
const INSTITUTE_CARD_TOKENIZE_KEY     = 'instituteCardTokenizePending';
const TUTOR_PENDING_KEY               = 'tutorOnboardingPendingPackage';
const STUDENT_PENDING_KEY             = 'studentOnboardingPendingPackage';
const INSTITUTE_PENDING_KEY           = 'instituteOnboardingPendingPackage';
const TUTOR_SETTINGS_PENDING_KEY      = 'tutorSettingsPendingPackage';
const STUDENT_SETTINGS_PENDING_KEY    = 'studentSettingsPendingPackage';
const INSTITUTE_SETTINGS_PENDING_KEY  = 'instituteSettingsPendingPackage';

const router    = useRouter();
const authStore = useAuthStore();

const isOnboardingReturn  = ref(false);
const isSettingsReturn    = ref(false);
const isCardTokenizeReturn = ref(false);
const isFreePlan          = ref(false);
const returnRoute         = ref('/tutorOnBoarding');

onMounted(() => {
    // Standalone card tokenization (add / replace card) — highest priority
    const studentCardTokenizeRaw   = sessionStorage.getItem(STUDENT_CARD_TOKENIZE_KEY);
    const tutorCardTokenizeRaw     = sessionStorage.getItem(TUTOR_CARD_TOKENIZE_KEY);
    const instituteCardTokenizeRaw = sessionStorage.getItem(INSTITUTE_CARD_TOKENIZE_KEY);

    if (studentCardTokenizeRaw || tutorCardTokenizeRaw || instituteCardTokenizeRaw) {
        isCardTokenizeReturn.value = true;
        if (studentCardTokenizeRaw) {
            sessionStorage.removeItem(STUDENT_CARD_TOKENIZE_KEY);
            returnRoute.value = '/studentSettings';
        } else if (tutorCardTokenizeRaw) {
            sessionStorage.removeItem(TUTOR_CARD_TOKENIZE_KEY);
            returnRoute.value = '/tutorSettings';
        } else if (instituteCardTokenizeRaw) {
            sessionStorage.removeItem(INSTITUTE_CARD_TOKENIZE_KEY);
            returnRoute.value = '/instituteSettings';
        }
        return;
    }

    // Read all pending keys and pick the most recent one (highest ts) to avoid stale key conflicts
    const tutorSettingsRaw      = sessionStorage.getItem(TUTOR_SETTINGS_PENDING_KEY);
    const studentSettingsRaw    = sessionStorage.getItem(STUDENT_SETTINGS_PENDING_KEY);
    const instituteSettingsRaw  = sessionStorage.getItem(INSTITUTE_SETTINGS_PENDING_KEY);
    const studentRaw   = sessionStorage.getItem(STUDENT_PENDING_KEY);
    const tutorRaw     = sessionStorage.getItem(TUTOR_PENDING_KEY);
    const instituteRaw = sessionStorage.getItem(INSTITUTE_PENDING_KEY);

    const getTs = (raw) => { try { return JSON.parse(raw)?.ts ?? 0; } catch { return 0; } };
    const settingsRaw   = tutorSettingsRaw || studentSettingsRaw || instituteSettingsRaw;
    const onboardingRaw = studentRaw || tutorRaw || instituteRaw;
    const settingsTs    = Math.max(getTs(tutorSettingsRaw), getTs(studentSettingsRaw), getTs(instituteSettingsRaw));
    const onboardingTs  = Math.max(getTs(studentRaw), getTs(tutorRaw), getTs(instituteRaw));
    const useSettings   = !!settingsRaw && (!onboardingRaw || settingsTs >= onboardingTs);

    if (useSettings) {
        // Settings subscription selection — key NOT cleared here; SubscriptionPage polls and clears it
        isSettingsReturn.value   = true;
        isOnboardingReturn.value = true;
        if (tutorSettingsRaw) {
            returnRoute.value = '/tutorSettings';
            sessionStorage.setItem('tutorSettingsPaymentSuccess', '1');
        } else if (instituteSettingsRaw) {
            returnRoute.value = '/instituteSettings';
            sessionStorage.setItem('instituteSettingsPaymentSuccess', '1');
        } else {
            returnRoute.value = '/studentSettings';
            sessionStorage.setItem('studentSettingsPaymentSuccess', '1');
        }
        // Clear any stale onboarding keys
        sessionStorage.removeItem(STUDENT_PENDING_KEY);
        sessionStorage.removeItem(TUTOR_PENDING_KEY);
        sessionStorage.removeItem(INSTITUTE_PENDING_KEY);
        return;
    }

    const raw = onboardingRaw;

    if (raw) {
        isOnboardingReturn.value = true;
        if (studentRaw)        returnRoute.value = '/studentOnBoarding';
        else if (instituteRaw) returnRoute.value = '/institutionOnBoarding';
        else                   returnRoute.value = '/tutorOnBoarding';

        try {
            const pending = JSON.parse(raw);
            isFreePlan.value = pending?.pricingType === 'free';
        } catch { /* ignore */ }

        // Tutor onboarding key cleared here — tutor payment plans page must never see a stale key
        // Student and institute keys are NOT cleared here — their payment plans pages consume them to auto-finalize
        if (tutorRaw)     sessionStorage.removeItem(TUTOR_PENDING_KEY);
        if (studentRaw)   sessionStorage.setItem('studentOnboardingPaymentSuccess', '1');
        if (instituteRaw) sessionStorage.setItem('instituteOnboardingPaymentSuccess', '1');
        // Clear any stale settings keys
        sessionStorage.removeItem(TUTOR_SETTINGS_PENDING_KEY);
        sessionStorage.removeItem(STUDENT_SETTINGS_PENDING_KEY);
        sessionStorage.removeItem(INSTITUTE_SETTINGS_PENDING_KEY);
    }
});

const returnToOnboarding = () => {
    router.push(returnRoute.value);
};

const goDashboard = () => {
    const role = authStore.user?.role?.toLowerCase();
    const routes = {
        'student':     '/student_dashboard',
        'tutor':       '/tutor_dashboard',
        'institute':   '/institution_dashboard',
        'institution': '/institution_dashboard',
        'admin':       '/admin_dashboard',
    };
    router.push(routes[role] || '/');
};
</script>

<style scoped>
.card {
    background: var(--surface-card);
    border: 1px solid var(--surface-border);
}

.continue-btn {
    background: #243ad7 !important;
    border-color: #243ad7 !important;
    color: #fff !important;
}

.continue-btn:hover {
    background: #1c2fb0 !important;
    border-color: #1c2fb0 !important;
}
</style>
